import { Command } from 'cmdk';

import { useSupplierGroupsPagination } from '../../../../admin/suppliergroup/hooks/useSupplierGroupsPagination';
import { useSupplierList } from '../../../../group/supplier/detail/hooks/useSupplierList';
import { formatCompanyName } from '../../../helpers/formatCompanyName.helper';
import { translate } from '../../../helpers/translate.helper';
import { useDebounce } from '../../../hooks/useDebounce';
import { GroupContextType } from '../../../utils/groupContextType';
import { useAutoSelectFirstCommandItem } from '../../hooks/useAutoSelectFirstCommandItem';
import { useGroupContextSelectionHandler } from '../../hooks/useGroupContextSelectionHandler';
import { useShortcutMenu } from '../../hooks/useShortcutMenu';
import { CommandEmptyState } from '../CommandEmptyState';
import { CommandErrorState } from '../CommandErrorState';
import { CommandItem } from '../CommandItem';
import { CommandSkeletonList } from '../CommandSkeletonList';

import { GroupContextSupplierGroupDetail } from './GroupContextSupplierGroupDetail';

export const SupplierGroupSelection = () => {
    const { search } = useShortcutMenu();
    const debouncedSearch = useDebounce(search, 300);
    const supplierGroupsQuery = useSupplierGroupsPagination({ searchQuery: debouncedSearch, page: 0 });
    const suppliersQuery = useSupplierList({ searchQuery: debouncedSearch });
    const { selectCompany } = useGroupContextSelectionHandler();

    const firstItemValue = supplierGroupsQuery.data?.content[0]
        ? `group-${supplierGroupsQuery.data.content[0].id}`
        : suppliersQuery.data?.[0]
          ? `supplier-${suppliersQuery.data[0].id}`
          : undefined;

    useAutoSelectFirstCommandItem(firstItemValue);

    if (supplierGroupsQuery.isPending || suppliersQuery.isPending) {
        return <CommandSkeletonList />;
    }

    if (supplierGroupsQuery.isError || suppliersQuery.isError) {
        return <CommandErrorState />;
    }

    const hasGroups = (supplierGroupsQuery.data?.content.length ?? 0) > 0;
    const hasCompanies = (suppliersQuery.data?.length ?? 0) > 0;

    if (!hasGroups && !hasCompanies) {
        return <CommandEmptyState />;
    }

    return (
        <Command.List>
            <Command.Group heading={translate('shortcutMenu.groups')}>
                {supplierGroupsQuery.data?.content.map((group) => (
                    <GroupContextSupplierGroupDetail key={group.id} supplierGroup={group} />
                ))}
            </Command.Group>
            <Command.Group heading={translate('shortcutMenu.companies')}>
                {suppliersQuery.data?.map((supplier) => (
                    <CommandItem
                        key={supplier.id}
                        value={`supplier-${supplier.id}`}
                        onSelect={() => {
                            void selectCompany({
                                companyId: supplier.id,
                                groupId: supplier.supplierGroupId,
                                type: GroupContextType.SUPPLIER_COMPANY,
                            });
                        }}
                    >
                        {formatCompanyName({ company: supplier })}
                    </CommandItem>
                ))}
            </Command.Group>
        </Command.List>
    );
};
